import React, { useMemo, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { useFormikContext, getIn } from 'formik';
import { getValue, standaloneRentersStates } from '@ourbranch/lookups';

import { useStore } from 'core/store';
import { OfferPageTabs } from 'core/store/offer-store';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import useSession from 'core/hooks/use-session';
import { isMegaDownPayRequired, canOfferMegaDownPay } from 'offer/helpers/mega-down-pay-helpers';
import { numberFormatter } from 'core/helpers/formatters';
import { track } from 'core/helpers/analytics';

import { OfferDetails } from '../offer-details';
import usePlanPrice from 'offer/hooks/use-plan-price';
import useDetailedPrice from 'offer/hooks/use-detailed-price';
import { PriceDetail } from './price-detail';
import { AutoBreakdownPreview } from 'common/components/auto/auto-breakdown-preview';
import OfferConfigurationModal from 'offer/components/settings/offer-configuration-modal';
import { CardLine } from '../card-line';
import useStyles from './price-details-card.styles';

const PriceDetailsCard = observer(() => {
  const classes = useStyles();
  const router = useHistory();
  const {
    offer: store,
    quote: { setPrefillData }
  } = useStore();
  const { offer } = store;
  const { values } = useFormikContext();
  const { canSkipOfferUnderwritingRules, user, isInternalAgent } = useSession();
  const [showOfferConfigModal, setShowOfferConfigModal] = useState(false);

  const toggleOfferConfigModal = useCallback(
    () => setShowOfferConfigModal(!showOfferConfigModal),
    [showOfferConfigModal]
  );

  const onRequoteClick = () => {
    track('Staff Requote - staff user', {
      message: 'Manual regenerate from price details',
      offerId: store.offer.id,
      staffUsername: user.username,
      webUserId: store.offer.webUserId,
      offerType: store.offer.quote.selectedOption
    });

    setPrefillData(offer);
    router.push('/quote');
  };

  const type = values.selectedOption || '';

  const showAuto = type.includes('A');
  const showHome = type.includes('H');
  const showCondo = type.includes('C');
  const showRenters = type.includes('R') && standaloneRentersStates[store.state];
  const showFarm = type.includes('F');

  // for the "fake" types we need the actual type to get the payments status
  const priceType = type === 'HB' || type === 'AB' ? 'HA' : type;
  const option = store.offer.options.find((opt) => opt.type === priceType);

  const prices = usePlanPrice(store.offer, priceType, useDetailedPrice);

  const { global } = store.offer.quote;

  const goToBillingDetailsTab = (scrollToString) => {
    store.setCurrentTab(OfferPageTabs.SETTINGS);
    store.setScrollTo(`${scrollToString}Settings`);
  };

  const homeMegaDownPay = useMemo(
    () =>
      (global.homeMegaDownPay &&
        canOfferMegaDownPay(global.homeEffectiveDate, global.homeBillingDayOfMonth, global.homeownersPaymentType)) ||
      isMegaDownPayRequired(global.homeEffectiveDate, global.homeBillingDayOfMonth, global.homeownersPaymentType) ||
      false,
    [global.homeMegaDownPay, global.homeEffectiveDate, global.homeBillingDayOfMonth, global.homeownersPaymentType]
  );

  const condoMegaDownPay = useMemo(
    () =>
      (global.condoMegaDownPay &&
        canOfferMegaDownPay(global.condoEffectiveDate, global.condoBillingDayOfMonth, global.condoPaymentType)) ||
      isMegaDownPayRequired(global.condoEffectiveDate, global.condoBillingDayOfMonth, global.condoPaymentType) ||
      false,
    [global.condoMegaDownPay, global.condoEffectiveDate, global.condoBillingDayOfMonth, global.condoPaymentType]
  );

  const autoMegaDownPay = useMemo(
    () =>
      (global.autoMegaDownPay &&
        canOfferMegaDownPay(global.autoEffectiveDate, global.autoBillingDayOfMonth, global.autoPaymentType)) ||
      isMegaDownPayRequired(global.autoEffectiveDate, global.autoBillingDayOfMonth, global.autoPaymentType) ||
      false,
    [global.autoMegaDownPay, global.autoEffectiveDate, global.autoBillingDayOfMonth, global.autoPaymentType]
  );

  const rentersMegaDownPay = useMemo(
    () =>
      (global.rentersMegaDownPay &&
        canOfferMegaDownPay(global.rentersEffectiveDate, global.rentersBillingDayOfMonth, global.rentersPaymentType)) ||
      isMegaDownPayRequired(global.rentersEffectiveDate, global.rentersBillingDayOfMonth, global.rentersPaymentType) ||
      false,
    [global.rentersMegaDownPay, global.rentersEffectiveDate, global.rentersBillingDayOfMonth, global.rentersPaymentType]
  );

  const farmMegaDownPay = useMemo(
    () =>
      (global.farmMegaDownPay &&
        canOfferMegaDownPay(global.farmEffectiveDate, global.farmBillingDayOfMonth, global.farmPaymentType)) ||
      isMegaDownPayRequired(global.farmEffectiveDate, global.farmBillingDayOfMonth, global.farmPaymentType) ||
      false,
    [global.farmMegaDownPay, global.farmEffectiveDate, global.farmBillingDayOfMonth, global.farmPaymentType]
  );

  return option ? (
    <Card id="offer_price_details" className={classes.card}>
      <div className={classes.header}>
        <Label type="infoSubtitleWhite">Price details</Label>
      </div>
      <CardLine className={classes.cardLine} />
      <div className={classes.content}>
        {showHome && (
          <PriceDetail
            name={option.name}
            type="Home"
            policyType={option.type}
            isMonthly={prices.isHomeMonthly}
            downPaymentDate={prices.homeDownPaymentDate}
            remainingPayments={prices.homeRemainingPayments}
            remainingPaymentsAmount={prices.homeRemainingPaymentsAmount}
            payment={prices.homePrice}
            paymentMethod={getValue('homeownersPaymentMethod', global.homeownersPaymentMethod)}
            paymentType={getValue('paymentType', global.homeownersPaymentType)}
            termTotal={numberFormatter(option?.homeTotal, 2)}
            stale={store.isStale}
            allowCollapsableContent
            nextPayments={prices.homeNextPayments}
            open={true}
            megaDownPay={homeMegaDownPay}
            goToBillingDetails={() => goToBillingDetailsTab('home')}
            effectiveDate={global.homeEffectiveDate}
          />
        )}
        {showCondo && (
          <PriceDetail
            name={option.name}
            type="Condo"
            policyType={option.type}
            isMonthly={prices.isCondoMonthly}
            downPaymentDate={prices.condoDownPaymentDate}
            remainingPayments={prices.condoRemainingPayments}
            remainingPaymentsAmount={prices.condoRemainingPaymentsAmount}
            payment={prices.condoPrice}
            paymentMethod={getValue('condoPaymentMethod', store.offer.quote.global.condoPaymentMethod)}
            paymentType={getValue('paymentType', store.offer.quote.global.condoPaymentType)}
            termTotal={numberFormatter(option.condoTotal, 2)}
            stale={store.isStale}
            allowCollapsableContent
            nextPayments={prices.condoNextPayments}
            open={true}
            megaDownPay={condoMegaDownPay}
            goToBillingDetails={() => goToBillingDetailsTab('condo')}
            effectiveDate={global.condoEffectiveDate}
          />
        )}
        {showAuto && (
          <>
            <PriceDetail
              name={option.name}
              type="Auto"
              policyType={option.type}
              isMonthly={prices.isAutoMonthly}
              downPaymentDate={prices.autoDownPaymentDate}
              remainingPayments={prices.autoRemainingPayments}
              remainingPaymentsAmount={prices.autoRemainingPaymentsAmount}
              payment={prices.autoPrice}
              paymentMethod={getValue('autoPaymentMethod', store.offer.quote.global.autoPaymentMethod)}
              paymentType={getValue('paymentType', store.offer.quote.global.autoPaymentType)}
              termTotal={numberFormatter(option.autoTotal, 2)}
              autoTotalAR={numberFormatter(prices.autoTotalAR, 2)}
              rentersTotalAR={numberFormatter(prices.rentersTotalAR, 2)}
              stale={store.isStale}
              allowCollapsableContent
              nextPayments={prices.autoNextPayments}
              open={true}
              megaDownPay={autoMegaDownPay}
              goToBillingDetails={() => goToBillingDetailsTab('auto')}
              effectiveDate={global.autoEffectiveDate}
            />
            <AutoBreakdownPreview
              offer={store.offer?.offer}
              cars={getIn(values, 'cars')}
              autoCoverage={getIn(values, 'autoCoverage')}
              policyType={store.selectedOption}
              trailers={getIn(values, 'trailers')}
            />
            {canSkipOfferUnderwritingRules && (
              <Button
                color="primary"
                variant="contained"
                className={classes.advancedConfigButton}
                onClick={toggleOfferConfigModal}
              >
                Advanced offer configuration
              </Button>
            )}

            {isInternalAgent && (
              <Button
                color="primary"
                variant="contained"
                className={classes.advancedConfigButton}
                onClick={onRequoteClick}
              >
                Regenerate quote from scratch
              </Button>
            )}
          </>
        )}
        {showRenters && (
          <PriceDetail
            name={option.name}
            type="Renters"
            policyType={option.type}
            isMonthly={prices.isRentersMonthly}
            downPaymentDate={prices.rentersDownPaymentDate}
            remainingPayments={prices.rentersRemainingPayments}
            remainingPaymentsAmount={prices.rentersRemainingPaymentsAmount}
            payment={prices.rentersPrice}
            paymentMethod={getValue('rentersPaymentMethod', store.offer.quote.global.rentersPaymentMethod)}
            paymentType={getValue('rentersPaymentType', store.offer.quote.global.rentersPaymentType)}
            termTotal={numberFormatter(option.rentersTotal, 2)}
            stale={store.isStale}
            standaloneRenters={standaloneRentersStates[store.state]}
            allowCollapsableContent
            nextPayments={prices.rentersNextPayments}
            open={true}
            megaDownPay={rentersMegaDownPay}
            goToBillingDetails={() => goToBillingDetailsTab('renters')}
            effectiveDate={global.rentersEffectiveDate}
          />
        )}
        {showFarm && (
          <PriceDetail
            name={option.name}
            type="Farm"
            policyType={option.type}
            isMonthly={prices.isFarmMonthly}
            downPaymentDate={prices.farmDownPaymentDate}
            remainingPayments={prices.farmRemainingPayments}
            remainingPaymentsAmount={prices.farmRemainingPaymentsAmount}
            payment={prices.farmPrice}
            paymentMethod={getValue('farmPaymentMethod', global.farmPaymentMethod)}
            paymentType={getValue('farmPaymentType', global.farmPaymentType)}
            termTotal={numberFormatter(option?.farmTotal, 2)}
            stale={store.isStale}
            allowCollapsableContent
            nextPayments={prices.farmNextPayments}
            open
            megaDownPay={farmMegaDownPay}
            goToBillingDetails={() => goToBillingDetailsTab('farm')}
            effectiveDate={global.farmEffectiveDate}
          />
        )}
      </div>
      <CardLine id="offer_price_section_divider" className={classes.cardLine} />
      <OfferDetails />
      {showOfferConfigModal && <OfferConfigurationModal open={showOfferConfigModal} onClose={toggleOfferConfigModal} />}
    </Card>
  ) : (
    <Card id="offer_price_details" className={classes.card}>
      <div className={classes.header}>
        <Label type="infoSubtitleWhite">Price details</Label>
      </div>
      <CardLine id="offer_price_section_divider" className={classes.cardLine} />
      <OfferDetails />
      {showOfferConfigModal && <OfferConfigurationModal open={showOfferConfigModal} onClose={toggleOfferConfigModal} />}
    </Card>
  );
});

export default PriceDetailsCard;
